import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, IconButton, TextField } from "@mui/material";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UtilService from "../../../services/UtilService";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import CustomSteteChip from "../../components/CustomStateChip";
import MiniPuzRow from "../../pzl_common/MiniPuzRow";
import { patternSearch } from "../../pzl_common/patternSearch";
import WordMeaning from "../../pzl_common/WordMeaning";
import WordList from "../../pzl_grid/board/candidates/WordList";
import WordValidation from "../../setting/banned_words/WordValidation";
import AdminGridService from "./AdminGridService";
import Logger from "../../../services/Logger";

export default function AdminGridDetail() {
    const { gridId } = useParams();
    const navigate = useNavigate();
    const [puzzle, setPuzzle] = useState();
    const [letters, setLetters] = useState();
    const [state, setState] = useState();
    const [gnerePath, setGenrePath] = useState();
    const [square, setSquare] = useState([]);
    const [wordsInUse, setWordsInUse] = useState([]);
    const [searchWord, setSearchWord] = useState("");
    const [positions, setPositions] = useState([]);
    const [showAllWords, setShowAllWords] = useState(false);
    const [selectedWordData, setSelectedWordData] = useState(null);
    const [definitionLoading, setDefinitionLoading] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState(false);
    const [message, setMessage] = useState("");
    const [openConfirm, setOpenConfirm] = useState(false);

    const [reload, setReload] = useState();
    const [isEditMode, setIsEditMode] = useState(false);
    const [editableLetters, setEditableLetters] = useState("");

    useEffect(() => {
        AdminGridService.requestPuzzleDetail(gridId, setPuzzle).then((r) => {
            // Logger.log(r);
            setLetters(r.letters);
            setState(r.state);

            // Convert letters to square array for PuzRow
            if (r.letters) {
                const rowLetters = r.letters.split("\n");
                const squareArray = rowLetters.map(row => row.split(""));
                setSquare(squareArray);
                setEditableLetters(r.letters);
            }

            // Set words in use from template
            if (r.template?.wordsInUse) {
                const words = r.template.wordsInUse.split("\n").filter(word => word.trim());
                setWordsInUse(words);
            }

            if (r.genre) {
                let path = [];
                if (r.genre.parentGenre) {
                    path.push(r.genre.parentGenre.name);
                }
                path.push(r.genre.name);

                setGenrePath(path.join("/"));
            } else {
                setGenrePath("No Genre. Click the genre button");
            }
        });
    }, [reload]);

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };


    const handleWordSearch = (e) => {
        const str = e.target.value.toUpperCase();
        setSearchWord(str);

        if (str && square.length > 0) {
            const foundPositions = patternSearch(square, str);
            setPositions(foundPositions);
        } else {
            setPositions([]);
        }
    };

    const fetchWordDefinition = async (word) => {
        try {
            setDefinitionLoading(true);
            const definition = await AdminGridService.getWordDefinition(word);
            // Ensure the data has the expected structure
            if (definition && Array.isArray(definition) && definition.length > 0) {
                setSelectedWordData(definition);
            } else {
                setSelectedWordData(null);
            }
        } catch (error) {
            Logger.error("Error fetching word definition:", error);
            setSelectedWordData(null);
        } finally {
            setDefinitionLoading(false);
        }
    };

    const handleSaveTitle = (_definition, _word) => {
        // This function can be implemented if needed for saving word definitions
        handleSnackBar("info", "Word definition functionality not implemented in this view");
    };

    const handleDeleteClick = () => {
        setOpenConfirm(true);
    };

    const handleDelete = (gridId) => {
        AdminGridService.deletePuzzle(gridId)
            .then(() => {
                handleSnackBar("success", "Puzzle deleted successfully");
                // Navigate back to the list after successful delete
                navigate("/admin/puzzle/grid");
            })
            .catch((error) => {
                handleSnackBar("error", error.message || "Failed to delete puzzle");
                Logger.error("Delete error:", error);
            });
        setOpenConfirm(false);
    };

    const handleEditClick = () => {
        setIsEditMode(true);
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        if (letters) {
            setEditableLetters(letters);
        }
    };

    const handleSaveLetters = () => {
        AdminGridService.updateLetters(gridId, editableLetters)
            .then(() => {
                handleSnackBar("success", "Puzzle grid updated successfully");
                setIsEditMode(false);
                setReload(!reload);
            })
            .catch((error) => {
                handleSnackBar("error", error.message || "Failed to update puzzle grid");
                Logger.error("Update error:", error);
            });
    };

    return (
        <Box>
            {puzzle && (
                <Grid container spacing={2}>
                    {/* Header with title, state, genre and settings */}
                    <Grid size={{ xs: 12 }}>
                        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid size={12}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, justifyContent: "space-between" }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Typography variant="h5">{puzzle.title}</Typography>
                                            <CustomSteteChip label={state} />
                                        </Box>
                                        <Button
                                            onClick={handleDeleteClick}
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            variant="outlined"
                                            size="small"
                                            aria-label="delete puzzle"
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                    <Typography variant="body1" color="text.secondary">
                                        Genre: {gnerePath}
                                    </Typography>

                                    {/* Dates */}
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Created: {UtilService.formatDate(puzzle.createdAt)}
                                            {puzzle.publishedAt && (
                                                <span> | Published: {UtilService.formatDate(puzzle.publishedAt)}</span>
                                            )}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Creator: {puzzle.cloudUser?.email}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Template: {puzzle.template?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Main content - split into two grids */}
                    <Grid size={4}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Word Search */}
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <TextField
                                    label="Word Search"
                                    value={searchWord}
                                    onChange={handleWordSearch}
                                    helperText="Type the word you want to find in the puzzle"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                />
                            </Paper>

                            {/* Word List */}
                            <WordList
                                wordList={wordsInUse}
                                selectedWord={null}
                                onWordSelect={(word) => {
                                    const upperWord = word.toUpperCase();
                                    setSearchWord(upperWord);
                                    if (square.length > 0) {
                                        const foundPositions = patternSearch(square, upperWord);
                                        setPositions(foundPositions);
                                    }
                                    // Fetch word definition
                                    fetchWordDefinition(word);
                                }}
                                showVisibilityToggle={true}
                                onVisibilityToggle={setShowAllWords}
                                showAllWords={showAllWords}
                                maxHeight={400}
                            />

                            {/* Word Validation */}
                            <WordValidation 
                                filledCoordinates={wordsInUse.map(word => ({ text: word }))} 
                                language={puzzle?.language || puzzle?.template?.language || 'english'}
                                isKids={puzzle?.isKids || false}
                                difficultyLevel={puzzle?.difficultyLevel || puzzle?.template?.difficultyLevel}
                            />

                            {/* Word Meaning */}
                            {selectedWordData && Array.isArray(selectedWordData) && selectedWordData.length > 0 && (
                                <WordMeaning
                                    wordData={selectedWordData}
                                    onSaveDefinition={handleSaveTitle}
                                    gridTplId={gridId}
                                    loading={definitionLoading}
                                />
                            )}
                        </Box>
                    </Grid>

                    <Grid size={8}>
                        <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6">
                                    Puzzle Grid
                                </Typography>
                                {!isEditMode ? (
                                    <IconButton
                                        onClick={handleEditClick}
                                        color="primary"
                                        aria-label="edit puzzle grid"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                ) : (
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <IconButton
                                            onClick={handleSaveLetters}
                                            color="primary"
                                            aria-label="save puzzle grid"
                                        >
                                            <SaveIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={handleCancelEdit}
                                            color="secondary"
                                            aria-label="cancel edit"
                                        >
                                            <CancelIcon />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>

                            {isEditMode ? (
                                <TextField
                                    label="Letters"
                                    multiline
                                    rows={12}
                                    variant="outlined"
                                    fullWidth
                                    value={editableLetters}
                                    onChange={(e) => setEditableLetters(e.target.value)}
                                    slotProps={{
                                        input: {
                                            style: { letterSpacing: "0.5em", fontFamily: "monospace" },
                                        },
                                    }}
                                />
                            ) : (
                                /* Puzzle display using MiniPuzRow */
                                <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                                    <Box>
                                        {square.map((row, rowIndex) => (
                                            <MiniPuzRow
                                                key={rowIndex}
                                                rowIndex={rowIndex}
                                                row={row}
                                                selectedWord={searchWord}
                                                positions={positions}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />

            <ConfirmDialog
                open={openConfirm}
                setOpen={setOpenConfirm}
                title="Delete Puzzle?"
                message="Would you like to delete this puzzle? This action cannot be undone."
                targetName={puzzle?.title}
                targetId={gridId}
                callback={handleDelete}
            />
        </Box>
    );
}

